import { noop } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import { Input, type InputProps } from "@/components/ui/input";
import { TypographyCaption } from "@/components/ui/typography";

type Props = InputProps & {
  name: string;
  label?: string;
  autoFocus?: boolean;
  description?: string;
  selectTextOnFocus?: boolean;
  optional?: boolean;
  required?: boolean;
};

const TextInputField = ({
  field,
  label,
  description,
  placeholder,
  selectTextOnFocus = false,
  onFocus = noop,
  optional = false,
  required = false,
  ...rest
}: Props & { field: any }) => {
  const { t } = useTranslation();
  const { error } = useFormField();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(field.value);

  useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  useEffect(() => {
    const handleBeforeSubmit = () => {
      if (
        document.activeElement === inputRef.current &&
        value !== field.value
      ) {
        field.onChange(value);
      }
    };

    const forms = inputRef.current?.closest("form");

    if (forms) {
      forms.addEventListener("submit", handleBeforeSubmit);
    }

    return () => {
      if (forms) {
        forms.removeEventListener("submit", handleBeforeSubmit);
      }
    };
  }, [field, value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue: string | null = event.target.value;
    if (newValue === "") {
      newValue = null;
    }
    setValue(newValue);
  };

  const handleBlur = (event: any) => {
    if (value !== field.value) {
      field.onChange(value);
    }
    field.onBlur();
    rest.onBlur?.(event);
  };

  return (
    <FormItem>
      {label && (
        <FormLabel>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </FormLabel>
      )}
      {optional && (
        <TypographyCaption className="text-muted-foreground capitalize !mt-0">
          {`(${t("optional")})`}
        </TypographyCaption>
      )}
      <FormControl>
        <Input
          {...field}
          {...rest}
          id={`input-${field.name}`}
          placeholder={placeholder}
          onFocus={(event) => {
            selectTextOnFocus && event.target.select();
            onFocus(field.value);
          }}
          ref={inputRef}
          value={value ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-invalid={error ? true : undefined}
        />
      </FormControl>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
};

export const TextInput = (props: Props) => {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={props.name}
      render={({ field }) => <TextInputField {...props} field={field} />}
    />
  );
};
